import { useCallback, useRef, useState } from 'react';
import { getSurveyQuestionsBySurveyIdAndSectionApi } from '../../../api/surveys';

// Questions of the currently selected section. Owns its own loading state and
// exposes reload() so "save then refresh" flows are a plain awaitable call
// instead of a redux flag another effect listens for.
const useSectionQuestions = (surveyId, jwt) => {
  const [surveyQuestions, setSurveyQuestions] = useState();
  const [isLoading, setIsLoading] = useState(false);
  const lastSectionCodeRef = useRef(null);

  // silent: refetch without flipping isLoading. The post-save refresh needs
  // this — toggling isLoading unmounts the question list, and the expanded
  // question must stay alive to finish its own reload and report back.
  const load = useCallback(
    async (sectionCode, { silent = false } = {}) => {
      if (!surveyId || !jwt || !sectionCode) return;
      lastSectionCodeRef.current = sectionCode;
      if (!silent) setIsLoading(true);
      try {
        const data = await getSurveyQuestionsBySurveyIdAndSectionApi(surveyId, sectionCode, jwt);
        setSurveyQuestions(data);
      } catch (e) {
        console.error(e);
      } finally {
        if (!silent) setIsLoading(false);
      }
    },
    [surveyId, jwt]
  );

  const reload = useCallback(
    (options) => {
      if (lastSectionCodeRef.current) {
        return load(lastSectionCodeRef.current, options);
      }
      return Promise.resolve();
    },
    [load]
  );

  const clear = useCallback(() => {
    lastSectionCodeRef.current = null;
    setSurveyQuestions(undefined);
  }, []);

  return { surveyQuestions, isLoading, load, reload, clear };
};

export default useSectionQuestions;
